import { Controller } from "@hotwired/stimulus"

// Searchable Ionicons picker for the Quick Links admin form.
// The hidden input is the only thing that posts; its value stays a raw
// Ionicons name string so the mobile API contract is unaffected.
export default class extends Controller {
  static targets = [
    "input",
    "trigger",
    "menu",
    "search",
    "list",
    "option",
    "previewIcon",
    "previewLabel",
    "empty",
  ]

  connect() {
    // Defensive: ensure the menu starts hidden
    if (this.hasMenuTarget && !this.menuTarget.classList.contains("hidden")) {
      this.menuTarget.classList.add("hidden")
    }

    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)

    this.boundClose = this.close.bind(this)
    document.addEventListener("turbo:before-visit", this.boundClose)
    window.addEventListener("popstate", this.boundClose)

    this.updatePreview()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("turbo:before-visit", this.boundClose)
    window.removeEventListener("popstate", this.boundClose)
  }

  toggle(event) {
    event.stopPropagation()
    const opening = this.menuTarget.classList.contains("hidden")
    this.menuTarget.classList.toggle("hidden")
    this.triggerTarget.setAttribute("aria-expanded", String(opening))

    if (opening) {
      document.dispatchEvent(
        new CustomEvent("dropdown:opened", { detail: { element: this.element } }),
      )
      this.searchTarget.value = ""
      this.filter()
      requestAnimationFrame(() => this.searchTarget.focus())
    }
  }

  close() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && !this.menuTarget.classList.contains("hidden")) {
      this.close()
      this.triggerTarget.focus()
    }
  }

  onSearchKeydown(event) {
    if (event.key === "Escape") {
      event.stopPropagation()
      this.close()
      this.triggerTarget.focus()
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      const first = this.optionTargets.find((o) => !o.classList.contains("hidden"))
      if (first) first.click()
    }
  }

  clickOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  // Multi-word filter: every search term must be contained in the option text.
  filter() {
    const search = this.searchTarget.value.toLowerCase().trim()
    const terms = search.length ? search.split(/\s+/) : []
    let visible = 0

    this.optionTargets.forEach((option) => {
      const haystack = (option.dataset.iconPickerSearch || "").toLowerCase()
      const match = terms.length === 0 || terms.every((term) => haystack.includes(term))
      option.classList.toggle("hidden", !match)
      if (match) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }
  }

  select(event) {
    this.inputTarget.value = event.currentTarget.dataset.iconPickerValue || ""
    this.updatePreview()
    this.close()
    this.triggerTarget.focus()
  }

  updatePreview() {
    const value = this.inputTarget.value

    if (this.hasPreviewIconTarget) {
      if (value) {
        this.previewIconTarget.setAttribute("name", value)
      } else {
        this.previewIconTarget.removeAttribute("name")
      }
    }

    if (this.hasPreviewLabelTarget) {
      this.previewLabelTarget.textContent = value || this.previewLabelTarget.dataset.noneLabel || ""
    }
  }
}
